import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { mh } from "@/lib/mh";
import { money } from "@/lib/onb";
import { Panel, Eyebrow } from "@/components/mh/bits";
import { SuggestionDiff } from "@/components/onb/LearningPanel";

const Bar = ({ actual, lo, hi }) => {
  const max = Math.max(hi, actual, 1);
  return (
    <div className="relative h-2 rounded-full bg-white/[0.05] overflow-hidden">
      <span className="absolute inset-y-0 bg-white/15 rounded-full" style={{ left: `${(lo / max) * 100}%`, width: `${((hi - lo) / max) * 100}%` }} />
      <span className="absolute inset-y-0 left-0 bg-[#D4FF3A] rounded-full transition-[width] duration-700" style={{ width: `${(actual / max) * 100}%` }} />
    </div>
  );
};

export const ProvenValue = ({ canEdit }) => {
  const [v, setV] = useState(null);
  const [sugs, setSugs] = useState([]);
  const [why, setWhy] = useState(false);
  const loadSugs = useCallback(() => mh.suggestions().then((r) => setSugs(r.data)).catch(() => {}), []);
  useEffect(() => { mh.value(30).then((r) => setV(r.data)).catch(() => {}); loadSugs(); }, [loadSugs]);
  if (!v) return null;
  const t = v.totals;
  const roles = v.roles.filter((r) => r.actual.runs || r.projected).slice(0, 12);
  return (
    <>
      <Panel cls="p-5 sm:p-6 space-y-4" testId="proven-value">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div><Eyebrow>Projected vs actual · last 30 days</Eyebrow>
            <div className="flex items-baseline gap-3 mt-2"><span className="font-display text-5xl text-[#D4FF3A]" data-testid="actual-money">{money(t.actual_money)}</span>
              <span className="text-sm text-muted-foreground">actual vs {money(t.projected_money_low)}–{money(t.projected_money_high)} projected</span></div>
            <p className="font-mono text-[11px] text-muted-foreground mt-1" data-testid="actual-hours">{t.actual_hours} h saved · {t.receipts} receipts · {t.unreported} runs without receipt</p></div>
          <button onClick={() => setWhy((x) => !x)} className="font-mono text-[11px] underline text-muted-foreground hover:text-foreground w-fit" data-testid="actual-method-btn">How we count</button>
        </div>
        {why && <p className="text-xs text-muted-foreground max-w-3xl" data-testid="actual-method">{v.method}</p>}
        <div className="space-y-3">
          {roles.map((r) => (
            <Link key={r.slug} to={`/atlas/${r.slug}`} className="grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(0,14rem)_1fr_auto] items-center gap-x-4 gap-y-1 hover:bg-white/[0.02] rounded-lg px-2 py-1.5 transition-colors" data-testid={`value-row-${r.slug}`}>
              <span className="text-sm truncate">{r.name}</span>
              <span className="hidden sm:block"><Bar actual={r.actual.money} lo={r.projected?.money_low || 0} hi={r.projected?.money_high || 0} /></span>
              <span className="font-mono text-[11px] text-right"><span className="text-[#D4FF3A]">{money(r.actual.money)}</span><span className="text-muted-foreground"> / {r.projected ? `${money(r.projected.money_low)}–${money(r.projected.money_high)}` : "—"}</span></span>
            </Link>
          ))}
          {roles.length === 0 && <p className="text-sm text-muted-foreground">No runs yet. Actual value appears as agents post receipts.</p>}
        </div>
      </Panel>
      {sugs.length > 0 && (
        <Panel cls="p-5 sm:p-6 space-y-3" testId="pending-suggestions">
          <Eyebrow>Improvements waiting · {sugs.length}</Eyebrow>
          {sugs.map((s) => (
            <div key={s.id}><Link to={`/atlas/${s.slug}`} className="font-mono text-[11px] underline text-muted-foreground">{s.agent_name}</Link>
              <SuggestionDiff s={s} canEdit={canEdit} onDecided={loadSugs} /></div>
          ))}
        </Panel>
      )}
    </>
  );
};
